import { Button, Flex } from "@chakra-ui/react";
import { Tooltip } from "../../../../../common/components/Tooltip";
import { type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { AwardCriterionType } from "../../../../../autogen/bff-api";
import { AWARD_CRITERION_TYPE_OPTIONS } from "../../../../../common/procurement/publicProcurementOptions";

// t('Only one price criterion is allowed')

interface Props {
  /**
   * The picked option value: an AwardCriterionType, the "ENVIRONMENT" grouping value, or "" before
   * anything is picked. Callers pass `criterionTypeSelectValue(type)` so every environment sub-type
   * collapses onto the one Miljø option.
   */
  value: string;
  /** True when some *other* criterion is the price one, which puts Price out of reach here. */
  hasOtherPriceCriterion: boolean;
  isDisabled?: boolean;
  /** Focus target when a dialog opens on the type question. */
  firstOptionRef?: RefObject<HTMLButtonElement>;
  onSelect: (typeValue: string) => void;
}

/**
 * The four award criterion types as a row of pills. It reads as a set of choices rather than a
 * dropdown that hides three of them, which matters because the type decides how the criterion is
 * scored at all — price rows come from the price model, the rest from an evaluator.
 */
export const CriterionTypePicker = ({
  value,
  hasOtherPriceCriterion,
  isDisabled = false,
  firstOptionRef,
  onSelect,
}: Props) => {
  const { t } = useTranslation();

  // "Other" rather than "any": the criterion that already is the price one keeps its own pill
  // reachable, or its type would read as unset.
  const priceTaken = (option: { value: string }) =>
    option.value === AwardCriterionType.Price && hasOtherPriceCriterion;

  // focus() does nothing on a disabled button, so aiming the dialog's opening focus at a pill that
  // is out of reach hands it back to Chakra — which lands on the close button. Price is the first
  // option and the one that goes out of reach, so the target has to be found, not assumed.
  const firstEnabled = AWARD_CRITERION_TYPE_OPTIONS.findIndex(
    (option) => !isDisabled && !priceTaken(option),
  );

  return (
    <Flex gap="2" wrap="wrap">
      {AWARD_CRITERION_TYPE_OPTIONS.map((option, optionIndex) => {
        const isSelected = value === option.value;
        const isPriceTaken = priceTaken(option);
        const pill = (
          <Button
            key={option.value}
            ref={optionIndex === firstEnabled ? firstOptionRef : undefined}
            // Same pill as the library panel's category filter — the two rows offer the same four
            // families, so they should not be two different-looking controls.
            size="xs"
            borderRadius="full"
            variant={isSelected ? "solid" : "outline"}
            colorScheme={isSelected ? "teal" : "gray"}
            aria-pressed={isSelected}
            isDisabled={isDisabled || isPriceTaken}
            onClick={() => onSelect(option.value)}
          >
            {t(option.labelKey)}
          </Button>
        );
        return isPriceTaken ? (
          // Chakra stops pointer events on a disabled button, so the tooltip needs a live wrapper —
          // without it the pill greys out with no reason given.
          <Tooltip key={option.value} label={t("Only one price criterion is allowed")}>
            <span>{pill}</span>
          </Tooltip>
        ) : (
          pill
        );
      })}
    </Flex>
  );
};
